<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import CapacityField from 'dashboard/routes/dashboard/settings/spaces/components/CapacityField.vue';

const props = defineProps({
  space: { type: Object, required: true },
  index: { type: Number, required: true },
  attributeDefinitions: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'toggle-included',
  'update-name',
  'update-capacity',
  'update-attribute',
]);

const { t } = useI18n();

const editingName = ref(false);
const tempName = ref('');
const activeEditKey = ref(null);
const detailsOpen = ref(false);

const CAPACITY_CONFIG = [
  {
    key: 'max_capacity_seated',
    label: 'SETTINGS.SPACES.MAX_CAPACITY_SEATED',
    icon: 'i-lucide-users',
    always: true,
  },
  {
    key: 'max_capacity_standing',
    label: 'SETTINGS.SPACES.MAX_CAPACITY_STANDING',
    icon: 'i-lucide-users',
    always: true,
  },
  {
    key: 'square_meters',
    label: 'SETTINGS.SPACES.SQUARE_METERS',
    icon: 'i-lucide-ruler',
    suffix: 'm²',
    step: '0.01',
  },
  {
    key: 'theater',
    label: 'SETTINGS.SPACES.THEATER',
    icon: 'i-lucide-presentation',
  },
  {
    key: 'classroom',
    label: 'SETTINGS.SPACES.CLASSROOM',
    icon: 'i-lucide-graduation-cap',
  },
  {
    key: 'banquet',
    label: 'SETTINGS.SPACES.BANQUET',
    icon: 'i-lucide-utensils',
  },
  {
    key: 'boardroom',
    label: 'SETTINGS.SPACES.BOARDROOM',
    icon: 'i-lucide-briefcase',
  },
  {
    key: 'u_shape',
    label: 'SETTINGS.SPACES.U_SHAPE',
    icon: 'i-lucide-move',
  },
  {
    key: 'standing',
    label: 'SETTINGS.SPACES.STANDING',
    icon: 'i-lucide-music',
  },
  {
    key: 'concert',
    label: 'SETTINGS.SPACES.CONCERT',
    icon: 'i-lucide-spotlight',
  },
  {
    key: 'cocktail',
    label: 'SETTINGS.SPACES.COCKTAIL',
    icon: 'i-lucide-wine',
  },
];

const isBoolean = val => typeof val === 'boolean' || val === 'true' || val === 'false';
const isNumeric = val => {
  if (val == null || val === '' || val === 'n/a' || isBoolean(val)) return false;
  return Number.isFinite(Number(val));
};
const formatCapacityValue = val => {
  if (val === true || val === 'true') return t('ONBOARDING_WIZARD.YES');
  if (val === false || val === 'false') return t('ONBOARDING_WIZARD.NO');
  return val;
};

const included = computed(() => props.space.included !== false);
const metadata = computed(() => props.space.metadata || {});

const imageUrl = computed(() => {
  const images = props.space.images || [];
  const first = images[0];
  if (!first) return null;
  const raw =
    typeof first === 'string'
      ? first
      : first.src || first.url || first.image_url || null;
  if (!raw) return null;
  if (/^(https?:|data:|blob:)/i.test(raw) || raw.startsWith('//')) {
    return raw.startsWith('//') ? `https:${raw}` : raw;
  }
  const base = props.space.source_url || '';
  if (!base) return raw;
  try {
    return new URL(raw, base).toString();
  } catch {
    return raw;
  }
});

const placeholderGradient = computed(() => {
  const colors = [
    'from-blue-400 to-blue-600',
    'from-purple-400 to-purple-600',
    'from-green-400 to-green-600',
    'from-orange-400 to-orange-600',
    'from-pink-400 to-pink-600',
    'from-teal-400 to-teal-600',
  ];
  const name = props.space.name || '';
  return colors[name.length % colors.length];
});

const maxCapacity = computed(() => {
  const maxFields = [
    metadata.value.max_capacity_seated,
    metadata.value.max_capacity_standing,
  ]
    .filter(isNumeric)
    .map(Number);
  if (maxFields.length) return Math.max(...maxFields);

  const layouts = [
    'theater',
    'classroom',
    'banquet',
    'boardroom',
    'u_shape',
    'concert',
    'cocktail',
    'standing',
  ]
    .map(key => metadata.value[key])
    .filter(isNumeric)
    .map(Number);
  return layouts.length ? Math.max(...layouts) : null;
});

const layoutBadges = computed(() =>
  CAPACITY_CONFIG.filter(
    config =>
      !config.always &&
      config.key !== 'square_meters' &&
      (isNumeric(metadata.value[config.key]) ||
        isBoolean(metadata.value[config.key]))
  ).map(config => ({
    key: config.key,
    label: t(config.label),
    value: formatCapacityValue(metadata.value[config.key]),
    icon: config.icon,
  }))
);

const editableCapacities = computed(() =>
  CAPACITY_CONFIG.filter(
    config =>
      config.always ||
      isNumeric(metadata.value[config.key]) ||
      isBoolean(metadata.value[config.key])
  )
);

const foundAttributes = computed(() => {
  const attrs = props.space.custom_attributes || {};
  return Object.keys(attrs).filter(
    key => attrs[key] != null && attrs[key] !== ''
  );
});

function attributeLabel(key) {
  return t(`SETTINGS.SPACES.ATTRIBUTES.${key.toUpperCase()}.LABEL`);
}

function attributeIcon(key) {
  return props.attributeDefinitions[key]?.icon || 'i-lucide-tag';
}

function attributeType(key) {
  return props.attributeDefinitions[key]?.type || 'boolean';
}

function startEditName() {
  tempName.value = props.space.name || '';
  editingName.value = true;
}

function saveName() {
  if (tempName.value.trim()) {
    emit('update-name', tempName.value.trim());
  }
  editingName.value = false;
}

function cancelEditName() {
  editingName.value = false;
}
</script>

<template>
  <article
    class="group relative flex flex-col overflow-hidden bg-white dark:bg-slate-900 border rounded-xl transition-all duration-200"
    :class="
      included
        ? 'border-woot-500 ring-1 ring-woot-500 shadow-sm'
        : 'border-slate-200 dark:border-slate-700 opacity-60'
    "
  >
    <div
      class="relative aspect-[16/9] overflow-hidden bg-slate-100 dark:bg-slate-800"
    >
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="space.name"
        referrerpolicy="no-referrer"
        class="w-full h-full object-cover"
      />
      <div
        v-else
        class="absolute inset-0 flex items-center justify-center bg-gradient-to-br"
        :class="placeholderGradient"
      >
        <Icon icon="i-lucide-orbit" class="size-14 text-white/80" />
      </div>

      <button
        type="button"
        class="absolute top-3 right-3 z-10 inline-flex items-center justify-center size-9 rounded-lg bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border border-slate-200 dark:border-slate-700 hover:border-woot-400 transition-colors"
        :aria-pressed="included"
        :aria-label="
          included
            ? t('ONBOARDING_WIZARD.REVIEW.EXCLUDE_SPACE')
            : t('ONBOARDING_WIZARD.REVIEW.INCLUDE_SPACE')
        "
        @click="emit('toggle-included')"
      >
        <Icon
          :icon="included ? 'i-lucide-check-square' : 'i-lucide-square'"
          class="size-5"
          :class="included ? 'text-woot-600' : 'text-slate-400'"
        />
      </button>

      <div
        v-if="maxCapacity"
        class="absolute bottom-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm shadow-sm"
      >
        <Icon
          icon="i-lucide-users"
          class="size-4 text-woot-600 dark:text-woot-400"
        />
        <span class="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {{ maxCapacity }}
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-3 p-4">
      <div>
        <div v-if="editingName" class="flex items-center gap-1">
          <input
            v-model="tempName"
            type="text"
            class="reset-base no-margin w-full text-base font-semibold text-slate-900 dark:text-white bg-transparent border-b-2 border-woot-500 focus:outline-none py-0.5"
            autofocus
            @keyup.enter="saveName"
            @keyup.escape="cancelEditName"
          />
          <button type="button" class="p-1 text-woot-600" @click="saveName">
            <Icon icon="i-lucide-check" class="size-4" />
          </button>
          <button
            type="button"
            class="p-1 text-slate-400"
            @click="cancelEditName"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
        <button
          v-else
          type="button"
          class="font-semibold text-base text-left text-slate-900 dark:text-slate-100 truncate w-full hover:text-woot-600"
          :title="t('SETTINGS.SPACES.CLICK_TO_EDIT')"
          @click="startEditName"
        >
          {{ space.name }}
        </button>

        <p
          v-if="space.description"
          class="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-0"
        >
          {{ space.description }}
        </p>

        <a
          v-if="space.source_url"
          :href="space.source_url"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-1 inline-flex items-center gap-1 text-xs text-woot-600 dark:text-woot-400 truncate max-w-full hover:underline"
        >
          <Icon icon="i-lucide-external-link" class="size-3 shrink-0" />
          <span class="truncate">{{
            space.source_url.replace(/^https?:\/\/(www\.)?/, '')
          }}</span>
        </a>
      </div>

      <div v-if="layoutBadges.length" class="flex items-center gap-2 flex-wrap">
        <div
          v-for="badge in layoutBadges"
          :key="badge.key"
          class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800"
          :title="`${badge.label}: ${badge.value}`"
        >
          <Icon
            :icon="badge.icon"
            class="size-3.5 text-slate-600 dark:text-slate-400"
          />
          <span class="text-xs font-medium text-slate-700 dark:text-slate-300">
            {{ badge.value }}
          </span>
        </div>
      </div>

      <button
        type="button"
        class="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-woot-600 self-start"
        @click="detailsOpen = !detailsOpen"
      >
        <Icon
          icon="i-lucide-chevron-down"
          class="size-4 transition-transform"
          :class="{ '-rotate-90': !detailsOpen }"
        />
        {{ t('ONBOARDING_WIZARD.REVIEW.EDIT_DETAILS') }}
      </button>

      <div
        v-if="detailsOpen"
        class="flex flex-col gap-1 pt-1 border-t border-slate-100 dark:border-slate-800"
        :class="{ 'pointer-events-none opacity-50': !included }"
      >
        <CapacityField
          v-for="config in editableCapacities"
          :key="config.key"
          :field-key="config.key"
          :active-edit-key="activeEditKey"
          :label="t(config.label)"
          :icon="config.icon"
          :value="metadata[config.key]"
          :suffix="config.suffix || ''"
          :step="config.step || '1'"
          :placeholder="t('SETTINGS.SPACES.CAPACITY_PLACEHOLDER')"
          @update="value => emit('update-capacity', config.key, value)"
          @start-edit="activeEditKey = $event"
          @end-edit="activeEditKey = null"
        />

        <div
          v-for="key in foundAttributes"
          :key="key"
          class="flex group rounded-lg items-center gap-2 px-3 py-1.5 transition-colors hover:bg-n-slate-2 dark:hover:bg-n-solid-1"
        >
          <div class="flex-shrink-0 w-40 flex items-center gap-2 min-w-0">
            <Icon
              :icon="attributeIcon(key)"
              class="size-4 opacity-50 text-woot-600 dark:text-woot-200 flex-shrink-0"
            />
            <p
              class="font-medium text-sm text-slate-900 dark:text-white truncate !m-0"
            >
              {{ attributeLabel(key) }}
            </p>
          </div>
          <div class="flex-1 min-w-0">
            <Switch
              v-if="attributeType(key) === 'boolean'"
              :model-value="!!space.custom_attributes[key]"
              class="my-1"
              @update:model-value="
                value => emit('update-attribute', key, value)
              "
            />
            <input
              v-else
              :value="space.custom_attributes[key]"
              type="text"
              class="w-full h-8 px-2 text-sm rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 !mb-0"
              @change="emit('update-attribute', key, $event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
